import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cn from 'classnames';
import {Form, Formik} from 'formik';
import {toast} from 'react-toastify';
import {useHistory} from 'react-router-dom';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import {
  ColorInput,
  ColorLabel,
  StoreAppearanceInputColorBlock,
  StoreAppearanceLeftContent,
  StoreAppearanceLeftContentBlock,
  StoreAppearanceLeftContentTitle,
  StoreAppearanceRightContent,
  StoreAppearanceRightContentTitle,
  StoreAppearanceToggleContent,
  StoreAppearanceToggleTitle,
  StoreAppearanceWrapper,
  TextInput,
  ColorPreview,
  ErrorMessage,
  StoreAppearanceImageBlock,
  StoreAppearanceImage,
  StoreAppearanceImageLabel,
  StoreAppearanceImageInput,
  StoreAppearanceImageLabelText,
  StoreAppearancePhone,
  StoreAppearancePhoneHeader,
  StoreAppearancePhoneContent,
  PhoneContentHeader,
  PhoneContentHeaderLogo,
  PhoneContentRestaurantName,
  PhoneContentRestaurantAddress,
  PhoneContentOpeningHours,
  PhoneContentToggle,
  PhoneContentScheduleText,
  PhoneContentCategoryText,
  PhoneContentItemBlock,
  PhoneContentItemImage,
  PhoneContentItemText,
  PhoneContentItemName,
  PhoneContentItemDescription,
  PhoneContentItemPrice,
  PhoneContentButton,
  PhoneContentButtonBlock,
  StoreAppearanceRightContentSubtitle,
  StoreAppearancePhoneHeaderBlock,
  StoreAppearancePhoneUrl,
  RemovePhotoIcon,
  StoreAppearanceImageActions,
  StoreAppearanceCoverImageBlock,
  StoreAppearanceImageUpload
} from 'routes/App/StoreAppearance/styles';
import {ButtonPrimary} from 'components/buttons';
import {
  DashboardField,
  DashboardInnerDescription
} from 'routes/App/dashboard/styles';
import SwitchS2E from 'components/Switch';
import StoreAppearanceSchema from 'helpers/services/formValidationSchemas/StoreAppearanceSchema';
import StoreAppearancePhoneHeaderImage from 'assets/store-appearance-phone-header.svg';
import StoreAppearancePhoneToggleImage from 'assets/store-appearance-phone-toggle.svg';
import StoreAppearancePhoneItemImage from 'assets/store-appearance-phone-item.svg';
import {DashboardHeaderActions} from 'components/DashboardHeaderActions/styles';
import {DELETE, UPLOAD} from 'store/global/uploadFile/action';
import {
  SET_LOADING,
  SAVE_DATA,
  GET_EMAIL_MARKETING_TRIAL_DATE
} from 'store/storeAppearance/action';
import AnimatePoints from 'components/AnimatePoints';
import LeaveModal from 'routes/App/StoreAppearance/modals/LeaveModal';
import isFormDirty from 'helpers/functionUtils/storeAppearance';
import Loading from 'components/shared/Utils/Loading';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {isEmailMarketingTrialExpired} from 'helpers/functionUtils/emailMarketing';

const StoreAppearance = () => {
  const formikRef = useRef();
  const history = useHistory();
  const dispatch = useDispatch();
  const {loading, saveSuccess, emailMarketingTrialDate} = useSelector(
    (state) => state.storeAppearanceReducer
  );
  const {activeLocation} = useSelector((state) => state.user);
  const DEFAULT_PRIMARY_COLOR = '#ff2d38';
  const DEFAULT_SECONDARY_COLOR = '#d40712';

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const [isInitialized, setIsInitialized] = useState(false);

  const contentRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (contentRef.current && backdropRef.current) {
      backdropRef.current.style.height = `${contentRef.current.scrollHeight}px`;
    }
  }, [loading]);

  useEffect(() => {
    if (saveSuccess) {
      formikRef.current?.resetForm();
    }
  }, [saveSuccess]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      formikRef?.current?.setFieldValue('welcomeLogoUrl', null);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const initialValues = useMemo(() => {
    if (!activeLocation?._id) {
      return {
        welcomeLogoUrl: null,
        topCoverImageUrl: null,
        isCustomThemeEnabled: false,
        themeMainColor: DEFAULT_PRIMARY_COLOR,
        themeSecondaryColor: DEFAULT_SECONDARY_COLOR
      };
    }

    return {
      welcomeLogoUrl: activeLocation.WelcomeLogoURL || null,
      topCoverImageUrl: activeLocation.TopCoverImageURL || null,
      isCustomThemeEnabled: Boolean(
        activeLocation.CustomTheme &&
          activeLocation.CustomTheme?.MainColor !== null &&
          activeLocation.CustomTheme?.SecondaryColor !== null
      ),
      themeMainColor:
        activeLocation.CustomTheme?.MainColor || DEFAULT_PRIMARY_COLOR,
      themeSecondaryColor:
        activeLocation.CustomTheme?.SecondaryColor || DEFAULT_SECONDARY_COLOR
    };
  }, [activeLocation]);

  useEffect(() => {
    if (initialValues?.welcomeLogoUrl) {
      setLogoPreview(initialValues.welcomeLogoUrl);
    }
    if (initialValues?.topCoverImageUrl) {
      setCoverPreview(initialValues.topCoverImageUrl);
    }
    setLogoFile(null);
    setCoverFile(null);
    setIsInitialized(true);
  }, [initialValues]);

  const openStorefront = () => {
    window.open(
      `${process.env.REACT_APP_STOREFRONT_URL}/order/${activeLocation?.Slug}/menu`,
      '_blank'
    );
  };

  const getStorefrontUrl = () => {
    return `${process.env.REACT_APP_STOREFRONT_URL}/order/${activeLocation?.Slug}/menu`
      ?.toString()
      ?.replace('https://', '');
  };

  const removeCoverImage = () => {
    setCoverFile(null);
    setCoverPreview(null);
  };

  const setLoading = (value) => {
    dispatch(SET_LOADING(value));
  };

  const uploadImage = (file) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      dispatch(
        UPLOAD({
          form: formData,
          setLoading,
          callback: (url) => {
            setLoading(false);
            if (!url) return reject(new Error('Image upload failed'));
            return resolve(url);
          }
        })
      );
    });
  };

  const deleteImageByName = (fileName) => {
    return new Promise((resolve, reject) => {
      dispatch(
        DELETE({
          file: fileName,
          setLoading,
          callback: (success) => {
            setLoading(false);
            if (!success) return reject(new Error('Image deletion failed'));
            return resolve(true);
          }
        })
      );
    });
  };

  useEffect(() => {
    dispatch(GET_EMAIL_MARKETING_TRIAL_DATE());
  }, [activeLocation?._id]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          if (!values.welcomeLogoUrl && !logoFile) {
            formikRef?.current?.setFieldError(
              'welcomeLogoUrl',
              'Restaurant logo is required'
            );
            return;
          }
          const body = {
            isCustomThemeEnabled: values?.isCustomThemeEnabled,
            themeMainColor: values?.themeMainColor,
            themeSecondaryColor: values?.themeSecondaryColor,
            welcomeLogoUrl: values?.welcomeLogoUrl
          };

          if (!values?.isCustomThemeEnabled) {
            body.themeMainColor = null;
            body.themeSecondaryColor = null;
          }

          try {
            let uploadedLogoUrl = values.welcomeLogoUrl || null;
            let uploadedCoverUrl = values.topCoverImageUrl || null;

            if (
              (values?.topCoverImageUrl && coverFile) ||
              (values?.topCoverImageUrl && !coverPreview)
            ) {
              const fileName = values.topCoverImageUrl?.split('/').pop();
              if (fileName) {
                await deleteImageByName(fileName);
              }
            }

            if (coverFile) {
              uploadedCoverUrl = await uploadImage(coverFile);
            }

            if (values?.welcomeLogoUrl && logoFile) {
              const fileName = values.welcomeLogoUrl?.split('/').pop();
              if (fileName) {
                await deleteImageByName(fileName);
              }
            }

            if (logoFile) {
              uploadedLogoUrl = await uploadImage(logoFile);
            }

            body.welcomeLogoUrl = uploadedLogoUrl;
            body.topCoverImageUrl = coverPreview ? uploadedCoverUrl : null;

            await dispatch(
              SAVE_DATA({locationId: activeLocation?._id, data: body})
            );
          } catch (error) {
            toast.error(
              error.message || 'Something went wrong during file upload'
            );
          }
        }}
        enableReinitialize
        innerRef={formikRef}
        validationSchema={StoreAppearanceSchema}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          setFieldValue,
          submitForm,
          dirty
        }) => (
          <Form>
            <DashboardContent
              ref={contentRef}
              className={cn('DashboardContent')}
            >
              <DashboardHeader className={cn('AlwaysFlex')}>
                <Headline className='mTop5'>Store Appearance</Headline>
                {activeLocation?.Slug && (
                  <DashboardHeaderActions>
                    <ButtonPrimary
                      className='ButtonMedium'
                      onClick={openStorefront}
                    >
                      <span>View Store</span>
                    </ButtonPrimary>
                  </DashboardHeaderActions>
                )}
              </DashboardHeader>
              <StoreAppearanceWrapper>
                <StoreAppearanceLeftContent>
                  <StoreAppearanceLeftContentBlock>
                    <StoreAppearanceLeftContentTitle>
                      Customize your store’s appearance to match your brand and
                      boost sales.
                    </StoreAppearanceLeftContentTitle>
                    <StoreAppearanceToggleContent>
                      <StoreAppearanceToggleTitle>
                        Storefront Logo & Cover
                      </StoreAppearanceToggleTitle>
                      <DashboardField className='paddingL0 paddingB5'>
                        <DashboardInnerDescription className='withIcon GreyText'>
                          <span>
                            To show on your storefront, upload both a logo and
                            cover image.
                          </span>
                        </DashboardInnerDescription>
                      </DashboardField>
                      <StoreAppearanceToggleTitle className='marginT25 marginB20'>
                        Restaurant Logo
                      </StoreAppearanceToggleTitle>
                      <StoreAppearanceImageBlock>
                        {logoPreview && (
                          <StoreAppearanceImage
                            src={logoPreview}
                            alt='Preview'
                            className='logoImg'
                          />
                        )}
                        <StoreAppearanceImageLabel className='logoImg'>
                          {!logoPreview && (
                            <StoreAppearanceImageUpload className='logo' />
                          )}
                          <StoreAppearanceImageLabelText>
                            <span className='redText'>Upload Logo</span>
                            <span>Recommended size: 400 x 400px</span>
                          </StoreAppearanceImageLabelText>
                          <StoreAppearanceImageInput
                            type='file'
                            accept='image/*'
                            onChange={handleLogoChange}
                          />
                        </StoreAppearanceImageLabel>
                      </StoreAppearanceImageBlock>
                      {errors?.welcomeLogoUrl && touched?.welcomeLogoUrl && (
                        <ErrorMessage>{errors?.welcomeLogoUrl}</ErrorMessage>
                      )}

                      <StoreAppearanceToggleTitle className='marginT25 marginB20'>
                        Cover image
                      </StoreAppearanceToggleTitle>
                      <StoreAppearanceImageBlock>
                        {coverPreview && (
                          <StoreAppearanceCoverImageBlock>
                            <StoreAppearanceImage
                              src={coverPreview}
                              alt='Preview'
                              style={{maxWidth: '300px', maxHeight: '60px'}}
                            />
                            <RemovePhotoIcon onClick={removeCoverImage} />
                          </StoreAppearanceCoverImageBlock>
                        )}
                        <StoreAppearanceImageActions>
                          <StoreAppearanceImageLabel className='coverImg'>
                            {!coverPreview && (
                              <StoreAppearanceImageUpload className='cover' />
                            )}
                            <StoreAppearanceImageLabelText>
                              <div className='redText'>Upload Image</div>
                              <div>
                                Use a 1920x300 pixel environmental photo that
                                will capture the environment at your store
                              </div>
                            </StoreAppearanceImageLabelText>
                            <StoreAppearanceImageInput
                              type='file'
                              accept='image/*'
                              onChange={handleCoverChange}
                            />
                          </StoreAppearanceImageLabel>
                        </StoreAppearanceImageActions>
                      </StoreAppearanceImageBlock>
                      <DashboardField className='paddingL0 paddingB5'>
                        <DashboardInnerDescription className='withIcon GreyText'>
                          <span>
                            Not seeing your logo or cover image on your
                            storefront? Make sure both the logo and cover image
                            are uploaded—they only appear when both are added.
                          </span>
                        </DashboardInnerDescription>
                      </DashboardField>
                    </StoreAppearanceToggleContent>
                    <StoreAppearanceToggleContent className='withBorder'>
                      <StoreAppearanceToggleTitle>
                        Storefront Colors
                      </StoreAppearanceToggleTitle>
                      <DashboardField className='w100 noWrap paddingL0 paddingT5 paddingB5'>
                        <SwitchS2E
                          labelPlacement='start'
                          label='Use Custom Color Scheme'
                          checked={values?.isCustomThemeEnabled}
                          onChange={handleChange}
                          name='isCustomThemeEnabled'
                        />
                      </DashboardField>
                      <DashboardField className='paddingL0 paddingT5 paddingB5'>
                        <DashboardInnerDescription className='withIcon GreyText'>
                          <span>
                            Toggle to apply your store’s custom color scheme, or
                            keep the default.
                          </span>
                        </DashboardInnerDescription>
                      </DashboardField>
                      {values?.isCustomThemeEnabled && (
                        <>
                          <StoreAppearanceToggleTitle className='marginT25'>
                            Main Color
                          </StoreAppearanceToggleTitle>
                          <StoreAppearanceInputColorBlock
                            data-error={
                              errors?.themeMainColor && touched?.themeMainColor
                            }
                          >
                            <ColorLabel>
                              <ColorInput
                                type='color'
                                value={values?.themeMainColor}
                                defaultValue={DEFAULT_PRIMARY_COLOR}
                                onChange={handleChange}
                                name='themeMainColor'
                              />
                              <ColorPreview color={values?.themeMainColor} />
                            </ColorLabel>

                            <TextInput
                              type='text'
                              value={values?.themeMainColor}
                              onChange={(e) =>
                                setFieldValue('themeMainColor', e.target.value)
                              }
                              name='themeMainColor'
                            />
                          </StoreAppearanceInputColorBlock>
                          {errors?.themeMainColor &&
                            touched?.themeMainColor && (
                              <ErrorMessage>
                                {errors?.themeMainColor}
                              </ErrorMessage>
                            )}
                          <DashboardField className='paddingL0 paddingT5 paddingB5'>
                            <DashboardInnerDescription className='withIcon GreyText marginT15'>
                              <span>
                                Controls the buttons as well as category title
                                text
                              </span>
                            </DashboardInnerDescription>
                          </DashboardField>
                          <StoreAppearanceToggleTitle className='marginT25'>
                            Secondary Color
                          </StoreAppearanceToggleTitle>
                          <StoreAppearanceInputColorBlock
                            data-error={
                              errors?.themeSecondaryColor &&
                              touched?.themeSecondaryColor
                            }
                          >
                            <ColorLabel>
                              <ColorInput
                                type='color'
                                value={values?.themeSecondaryColor}
                                defaultValue={DEFAULT_SECONDARY_COLOR}
                                onChange={handleChange}
                                name='themeSecondaryColor'
                              />
                              <ColorPreview
                                color={values?.themeSecondaryColor}
                              />
                            </ColorLabel>

                            <TextInput
                              type='text'
                              value={values?.themeSecondaryColor}
                              onChange={(e) =>
                                setFieldValue(
                                  'themeSecondaryColor',
                                  e.target.value
                                )
                              }
                              name='themeSecondaryColor'
                            />
                          </StoreAppearanceInputColorBlock>
                          {errors?.themeSecondaryColor &&
                            touched?.themeSecondaryColor && (
                              <ErrorMessage>
                                {errors?.themeSecondaryColor}
                              </ErrorMessage>
                            )}
                          <DashboardField className='paddingL0 paddingT5 paddingB5'>
                            <DashboardInnerDescription className='withIcon GreyText marginT15'>
                              <span>Controls the buttons hover state</span>
                            </DashboardInnerDescription>
                          </DashboardField>
                        </>
                      )}
                    </StoreAppearanceToggleContent>
                  </StoreAppearanceLeftContentBlock>
                  <DashboardField className='justifyCenter'>
                    <ButtonPrimary
                      onClick={submitForm}
                      className={cn({
                        disabled:
                          loading ||
                          !isFormDirty({
                            dirty,
                            logoFile,
                            coverFile,
                            logoPreview,
                            coverPreview,
                            initialLogoUrl: initialValues?.welcomeLogoUrl,
                            initialCoverUrl: initialValues?.topCoverImageUrl,
                            isInitialized
                          })
                      })}
                      type='button'
                    >
                      {loading ? (
                        <span>
                          Saving
                          <AnimatePoints className='White' />
                        </span>
                      ) : (
                        <span>Save Changes</span>
                      )}
                    </ButtonPrimary>
                  </DashboardField>
                </StoreAppearanceLeftContent>
                <div>
                  <StoreAppearanceRightContent>
                    <StoreAppearanceRightContentTitle>
                      Store Preview
                    </StoreAppearanceRightContentTitle>
                    <StoreAppearanceRightContentSubtitle>
                      See how your store will look
                    </StoreAppearanceRightContentSubtitle>
                    <StoreAppearancePhone>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <StoreAppearancePhoneHeaderBlock>
                          <StoreAppearancePhoneHeader
                            src={StoreAppearancePhoneHeaderImage}
                            alt='Heared'
                          />
                          {activeLocation?.Slug && (
                            <StoreAppearancePhoneUrl title={getStorefrontUrl()}>
                              {getStorefrontUrl()}
                            </StoreAppearancePhoneUrl>
                          )}
                        </StoreAppearancePhoneHeaderBlock>
                        <StoreAppearancePhoneContent>
                          {logoPreview && coverPreview && (
                            <PhoneContentHeader coverImage={coverPreview}>
                              <PhoneContentHeaderLogo>
                                <img
                                  src={logoPreview}
                                  alt='Logo'
                                  className='logoImg'
                                />
                              </PhoneContentHeaderLogo>
                            </PhoneContentHeader>
                          )}
                          <PhoneContentRestaurantName>
                            {activeLocation?.BusinessName ||
                              activeLocation?.FullName ||
                              'Restaurant Name'}
                          </PhoneContentRestaurantName>
                          <PhoneContentRestaurantAddress>
                            {activeLocation?.Address ||
                              '123 Main St, City, Country'}
                          </PhoneContentRestaurantAddress>
                          <PhoneContentOpeningHours>
                            <span className='closedText'>Closed</span>
                            <span className='dotIcon'>•</span>
                            <span>Opens at 8:30 AM</span>
                          </PhoneContentOpeningHours>
                          <PhoneContentToggle
                            src={StoreAppearancePhoneToggleImage}
                            alt='Toggle'
                          />
                          <PhoneContentScheduleText>
                            <span>Scheduled: HH:MM AM/PM MM/DD/YY</span>
                            <span className='boldText'>Edit</span>
                          </PhoneContentScheduleText>
                          {[1, 2].map((category) => (
                            <div key={`category-${category}`}>
                              <PhoneContentCategoryText>
                                Category {category}
                              </PhoneContentCategoryText>
                              {[1, 2, 3].map((item) => (
                                <PhoneContentItemBlock key={`item-${item}`}>
                                  <PhoneContentItemText>
                                    <PhoneContentItemName>
                                      Item Name {item}
                                    </PhoneContentItemName>
                                    <PhoneContentItemDescription>
                                      Item description
                                    </PhoneContentItemDescription>
                                    <PhoneContentItemPrice
                                      mainColor={
                                        values?.isCustomThemeEnabled
                                          ? values?.themeMainColor
                                          : DEFAULT_PRIMARY_COLOR
                                      }
                                    >
                                      <span>$9.99</span>
                                      <span className='outOfStockText'>
                                        Out of Stock
                                      </span>
                                    </PhoneContentItemPrice>
                                  </PhoneContentItemText>
                                  <PhoneContentItemImage
                                    src={StoreAppearancePhoneItemImage}
                                    alt='Item'
                                  />
                                </PhoneContentItemBlock>
                              ))}
                            </div>
                          ))}
                        </StoreAppearancePhoneContent>
                        <PhoneContentButtonBlock>
                          <PhoneContentButton
                            mainColor={
                              values?.isCustomThemeEnabled
                                ? values?.themeMainColor
                                : DEFAULT_PRIMARY_COLOR
                            }
                            secondaryColor={
                              values?.isCustomThemeEnabled
                                ? values?.themeSecondaryColor
                                : DEFAULT_SECONDARY_COLOR
                            }
                          >
                            <span>View cart (0)</span>
                          </PhoneContentButton>
                        </PhoneContentButtonBlock>
                      </div>
                    </StoreAppearancePhone>
                  </StoreAppearanceRightContent>
                  {(activeLocation?.AllowedServiceFeatures?.includes(
                    'email_campaigns_active'
                  ) ||
                    (emailMarketingTrialDate &&
                      !isEmailMarketingTrialExpired(
                        emailMarketingTrialDate,
                        activeLocation?.Timezone
                      ))) && (
                    <StoreAppearanceRightContent className='marketingDisclaimer'>
                      <span className='boldItalicText'>
                        Running Email Campaigns?
                      </span>{' '}
                      Your logo, cover, and colors will appear there too —
                      making your emails instantly recognizable and on-brand.
                    </StoreAppearanceRightContent>
                  )}
                </div>
              </StoreAppearanceWrapper>
              {loading && (
                <Loading
                  ref={backdropRef}
                  className='LoadingWrapperFullContent'
                />
              )}
            </DashboardContent>
            <LeaveModal
              when={isFormDirty({
                dirty,
                logoFile,
                coverFile,
                logoPreview,
                coverPreview,
                initialLogoUrl: initialValues?.welcomeLogoUrl,
                initialCoverUrl: initialValues?.topCoverImageUrl,
                isInitialized
              })}
              navigate={(path) => history.push(path)}
              message='You have unsaved changes. Leave anyway?'
              onConfirm={() => {
                formikRef?.current?.submitForm();
              }}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default StoreAppearance;
